import {useLayoutEffect, useState} from 'react';

const NARROW_QUERY = '(max-width: 991.98px)';

export default function usePropertyWorkspaceLayout(containerRef) {
    const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);
    const [height, setHeight] = useState(undefined);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;
        const media = window.matchMedia(NARROW_QUERY);
        const viewport = window.visualViewport;
        let frame;
        const measure = () => {
            const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
            const next = Math.max(0, Math.floor(bottom - container.getBoundingClientRect().top));
            setHeight(current => current === next ? current : next);
        };
        const scheduleMeasure = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(measure);
        };
        const updateBreakpoint = () => setIsNarrow(media.matches);
        measure();
        updateBreakpoint();

        // Navigation can wrap or change height without a window resize. Observe the
        // preceding layout as well as the parent, rather than assuming a fixed nav height.
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleMeasure);
        if (container.parentElement) observer?.observe(container.parentElement);
        for (let sibling = container.previousElementSibling; sibling; sibling = sibling.previousElementSibling) {
            observer?.observe(sibling);
        }
        media.addEventListener('change', updateBreakpoint);
        window.addEventListener('resize', scheduleMeasure);
        viewport?.addEventListener('resize', scheduleMeasure);
        viewport?.addEventListener('scroll', scheduleMeasure);
        return () => {
            cancelAnimationFrame(frame);
            observer?.disconnect();
            media.removeEventListener('change', updateBreakpoint);
            window.removeEventListener('resize', scheduleMeasure);
            viewport?.removeEventListener('resize', scheduleMeasure);
            viewport?.removeEventListener('scroll', scheduleMeasure);
        };
    }, [containerRef]);

    return {height, isNarrow};
}
